<template>
  <component
    :is="linkTag"
    :href="linkHref"
    :target="linkTarget"
    :to="linkTo"
    :data-id="id"
    :type="linkButtonType"
    :aria-label="ariaLabel || id"
    tabindex="0"
    class="anchor"
    :class="{
      'anchor--full-width': fullWidth,
    }"
    :role="linkRoleType"
    @click="handleClick"
    @mousedown.middle="handleMiddleClick"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
import type { RouteLocation } from 'vue-router';

export type AnchorProps = {
  href?: string;
  to?: string | RouteLocation;
  target?: string;
  id?: string;
  fullWidth?: boolean;
  hideFromCrawl?: boolean;
  submit?: boolean;
  roleButton?: boolean;
  ariaLabel?: string;
};

export type LinkEmits = {
  (name: 'click', event: PointerEvent): void;
  (name: 'priorityClick', event: PointerEvent): void;
};

const emit = defineEmits<LinkEmits>();
const props = withDefaults(defineProps<AnchorProps>(), {
  href: undefined,
  to: undefined,
  target: undefined,
  id: undefined,
  fullWidth: undefined,
  hideFromCrawl: undefined,
  submit: undefined,
  roleButton: undefined,
  ariaLabel: undefined,
});

const linkTarget = computed(() => props.target || undefined);
const { linkTag, linkTo, linkHref, linkButtonType, linkRoleType } = useLinks(props, 'button');

const { handleClick, handleMiddleClick } = useHideFromCrawl(props, emit);
</script>

<style lang="scss" scoped>
.anchor {
  position: relative;
  display: inline-block;
  overflow: visible;
  vertical-align: middle;
  width: auto;
  max-width: 100%;
  margin: 0;
  padding: 0;
  background-color: transparent;
  background-image: none;
  border: 0;
  border-radius: 0;
  outline: 0;
  text-transform: none;
  text-decoration: none;
  text-align: inherit;
  text-shadow: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  cursor: pointer;
  color: inherit;
  appearance: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

  &.anchor--full-width {
    display: block;
    width: 100%;
  }

  &:focus-visible {
    @include focus-outline-layered;
  }
}
</style>
